/**
 * Animation variants for Framer Motion
 * These can be reused across components for consistent animations
 */

// Fade in animation
export const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

// Fade in from bottom
export const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

// Fade in from left
export const fadeInLeft = {
    hidden: { opacity: 0, x: -20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

// Fade in from right
export const fadeInRight = {
    hidden: { opacity: 0, x: 20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

// Scale up animation
export const scaleUp = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.5,
            ease: "easeOut"
        }
    }
};

// Staggered container animation
export const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2,
        }
    }
};

// Staggered item animation
export const staggerItem = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 10
        }
    }
};

// Hover animations
export const hoverScale = {
    scale: 1.05,
    transition: { duration: 0.3 }
};

export const hoverElevate = {
    y: -5,
    boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
    transition: { duration: 0.3 }
};

// Button hover animation
export const buttonHover = {
    scale: 1.05,
    backgroundColor: "#2563EB", // Tailwind blue-600
    transition: { duration: 0.2 }
};

// Enhanced button hover with glow effect
export const buttonHoverGlow = {
    scale: 1.05,
    boxShadow: "0 0 20px rgba(59, 130, 246, 0.5)",
    transition: { duration: 0.3 }
};

// Enhanced interactive hover effects
export const linkHover = {
    scale: 1.02,
    color: "#2563EB", // Tailwind blue-600
    transition: { duration: 0.2, ease: "easeOut" }
};

export const navLinkHover = {
    scale: 1.05,
    y: -2,
    transition: { duration: 0.2, ease: "easeOut" }
};

export const socialIconHover = {
    scale: 1.3,
    y: -3,
    rotate: [0, -5, 5, 0],
    transition: { duration: 0.3, ease: "easeOut" }
};

// Filter button animations
export const filterButtonActive = {
    scale: 1.05,
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    transition: { duration: 0.2 }
};

export const filterButtonHover = {
    scale: 1.02,
    backgroundColor: "#E5E7EB",
    transition: { duration: 0.2 }
};

// Card hover animations
export const cardHover = {
    y: -8,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.3, ease: "easeOut" }
};

export const cardHoverScale = {
    scale: 1.02,
    y: -5,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    transition: { duration: 0.3, ease: "easeOut" }
};

// Interactive element hover effects
export const interactiveHover = {
    scale: 1.05,
    transition: { duration: 0.2, ease: "easeOut" }
};

export const iconHover = {
    scale: 1.2,
    rotate: 5,
    transition: { duration: 0.2, ease: "easeOut" }
};

// Page transitions
export const pageTransition = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: {
            duration: 0.5,
            when: "beforeChildren",
            staggerChildren: 0.1
        }
    },
    exit: {
        opacity: 0,
        transition: { duration: 0.3 }
    }
};

// Section entrance animations
export const sectionFadeIn = {
    hidden: {
        opacity: 0,
        y: 50
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};

export const sectionSlideIn = {
    hidden: {
        opacity: 0,
        x: -50
    },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};

// Loading animations
export const loadingPulse = {
    scale: [1, 1.05, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
        repeat: Infinity,
        duration: 1.5
    }
};

export const loadingSpinner = {
    rotate: 360,
    transition: {
        repeat: Infinity,
        duration: 1,
        ease: "linear"
    }
};

export const loadingSkeleton = {
    opacity: [0.4, 0.8, 0.4],
    transition: {
        repeat: Infinity,
        duration: 1.5,
        ease: "easeInOut"
    }
};

// Enhanced loading states
export const loadingDots = {
    scale: [1, 1.2, 1],
    transition: {
        repeat: Infinity,
        duration: 0.8,
        ease: "easeInOut"
    }
};

export const loadingFade = {
    opacity: [0.3, 1, 0.3],
    transition: {
        repeat: Infinity,
        duration: 2,
        ease: "easeInOut"
    }
};

// Smooth page transitions
export const pageSlideIn = {
    initial: { opacity: 0, x: 100 },
    animate: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    },
    exit: {
        opacity: 0,
        x: -100,
        transition: {
            duration: 0.4,
            ease: "easeIn"
        }
    }
};

// Content transition animations
export const contentSlide = {
    initial: { opacity: 0, x: 20 },
    animate: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.5, ease: "easeOut" }
    },
    exit: {
        opacity: 0,
        x: -20,
        transition: { duration: 0.3, ease: "easeIn" }
    }
};

export const contentFade = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: { duration: 0.4 }
    },
    exit: {
        opacity: 0,
        transition: { duration: 0.2 }
    }
};